import {
    Box,
    Caption,
    Checkbox,
    LinkText,
    Status,
    StatusBadge,
    TextCell,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from "@bcc-ui/ui-components";
import { t } from "i18next";
import React from "react";
import { CellProps, Column } from "react-table";

import CopyToClipboard from "components/copy-to-clipboard/CopyToClipboard";
import { generateUserTypesToTranslationMap } from "components/users/add-user/AddUserForm";
import UserKebabMenu from "components/users/UserKebabMenu";
import { ssoLoginMethodList } from "domain/tenants";
import { UserType } from "domain/user";
import { Group, UserTableData } from "domain/users";
import { LicensePoolList } from "services/licenses/LicensePoolService";
import { formatDateWithoutTime } from "utils/format";

import testIds from "testIds.json";

const commonCellRendered = {
    disableResizing: false,
    disableSortBy: true,
    minWidth: 90,
    Cell: ({ value }: CellProps<Report>) => <>{value}</>,
};

export const createColumns = (
    hasLicensePoolFeature: boolean,
    canAssignLicensePool: boolean,
    hasAllUsersSelected: () => boolean,
    updateUserState: (value: boolean) => void,
    isAnyUsersSelected: () => boolean,
    isUuidSelected: (uuid: string) => boolean,
    deselected: boolean,
    handleCheckboxChange: (event: React.ChangeEvent<HTMLInputElement>, uuid: string) => void,
    userUuid: string | undefined,
    licensePoolsList: LicensePoolList[],
    isUserTypeStandard: (userType: string) => boolean,
    onUserDelete: () => void,
    onUserEdit: () => void,
    selectedUser: (uuid: string) => void,
    showDetailsView: (value: boolean) => void,
    userTypeTooltipsTranslationMap: Map<UserType, string>,
    tableContainerRef: React.RefObject<HTMLDivElement>,
    userGroupAccess: boolean,
    licensePoolsAccess: boolean,
    showGroupInitials: (groups: Group[]) => React.JSX.Element
) => {
    const columns: Array<Column<UserTableData>> = [];

    hasLicensePoolFeature &&
        canAssignLicensePool &&
        columns.push({
            ...commonCellRendered,
            id: "selection",
            Header: () => (
                <Checkbox
                    name={"multiselect"}
                    testIds={{ input: testIds.common.primaryView.table.selectAllCheckbox }}
                    checked={hasAllUsersSelected()}
                    indeterminate={isAnyUsersSelected() && !hasAllUsersSelected()}
                    onChange={(event) => {
                        if (event.target.checked) {
                            updateUserState(true);
                        } else {
                            updateUserState(!hasAllUsersSelected());
                        }
                    }}
                    hideLabelSection
                />
            ),
            Cell: ({ row }: CellProps<UserTableData>) => {
                const isLoggedInUser = userUuid === row.original.uuid;

                if (isLoggedInUser) {
                    return null;
                }

                return (
                    <Checkbox
                        testIds={{ input: testIds.common.primaryView.table.selectRowCheckbox }}
                        key={row.original.uuid}
                        id={row.original.uuid}
                        name="uuids"
                        checked={isUuidSelected(row.original.uuid) && !deselected}
                        value={row.original.uuid}
                        onChange={(e) => handleCheckboxChange(e, row.original.uuid)}
                        hideLabelSection
                    />
                );
            },
            disableResizing: true,
            minWidth: 60,
            width: 60,
        });

    columns.push({
        Header: <TextCell key="name">{t("Common.name")}</TextCell>,
        accessor: "name",
        Cell: (cellInfo) => (
            <TextCell>
                <Box verticalAlignment="Center" gap={"Xxs"}>
                    <UserKebabMenu
                        uuid={cellInfo.cell.row.original.uuid}
                        userName={
                            cellInfo.value == null || cellInfo.value.trim() === ""
                                ? cellInfo.cell.row.original.email
                                : cellInfo.value
                        }
                        userRoleUuid={cellInfo.cell.row.original.roleUuid}
                        email={cellInfo.cell.row.original.email}
                        expirationDate={cellInfo.cell.row.original.expirationDate}
                        onUserDelete={onUserDelete}
                        onUserEdit={onUserEdit}
                        enabled={cellInfo.cell.row.original.enabled}
                        emailVerified={cellInfo.cell.row.original.emailVerified}
                        loginMethod={cellInfo.cell.row.original.loginMethod}
                        userLicensePoolUuid={cellInfo.cell.row.original.poolName}
                        userType={cellInfo.cell.row.original.userType}
                        licensePoolsList={licensePoolsList}
                        tableRef={tableContainerRef}
                    />
                    <Box
                        columnLayout={!isUserTypeStandard(cellInfo.cell.row.original.userType)}
                        horizontalAlignment={"Left"}
                        verticalAlignment={"Top"}
                    >
                        <LinkText
                            as={"button"}
                            onClick={() => {
                                selectedUser(cellInfo.cell.row.original.uuid);
                                showDetailsView(true);
                            }}
                        >
                            {cellInfo.value == null || cellInfo.value?.trim() === ""
                                ? cellInfo.cell.row.original.email
                                : cellInfo.value}
                        </LinkText>
                        {!isUserTypeStandard(cellInfo.cell.row.original.userType) && (
                            <Tooltip key={"userTypeTooltip"}>
                                <TooltipTrigger>
                                    <Caption greyTextStyle>
                                        {generateUserTypesToTranslationMap(t).get(cellInfo.cell.row.original.userType)}
                                    </Caption>
                                </TooltipTrigger>
                                <TooltipContent>
                                    {userTypeTooltipsTranslationMap.get(cellInfo.cell.row.original.userType)}
                                </TooltipContent>
                            </Tooltip>
                        )}
                    </Box>
                </Box>
            </TextCell>
        ),
        width: 220,
    });

    userGroupAccess &&
        columns.push({
            Header: <TextCell key="groups">{t("UsersTable.groups")}</TextCell>,
            accessor: "groups",
            Cell: ({ cell: { value } }) => <div>{showGroupInitials(value ? value : [])}</div>,
            width: 180,
        });

    columns.push(
        {
            Header: <TextCell key="status">{t("UsersTable.userStatus.title")}</TextCell>,

            accessor: "enabled",
            Cell: ({ cell: { value } }) => (
                <StatusBadge
                    value={
                        value ? t("UsersTable.userStatus.confirmed") : t("UsersTable.userStatus.forceChangePassword")
                    }
                    status={value ? Status.SUCCESS : Status.ERROR}
                />
            ),
            width: 150,
        },

        {
            Header: <TextCell key="email">{t("Common.email")}</TextCell>,
            accessor: "email",
            Cell: ({ cell: { value } }) => (
                <TextCell>
                    <Box>
                        {value}
                        <CopyToClipboard value={value} isNewTable />
                    </Box>
                </TextCell>
            ),
            width: 150,
        },
        {
            Header: <TextCell key="emailVerified">{t("UsersTable.emailStatus")}</TextCell>,
            accessor: "emailVerified",
            Cell: ({ row }) =>
                ssoLoginMethodList.includes(row.original.loginMethod) ? (
                    <StatusBadge status={Status.INFO} value={t("UsersTable.ssoUsers")} />
                ) : row.original.emailVerified ? (
                    <StatusBadge status={Status.SUCCESS} value={t("UsersTable.emailVerified")} />
                ) : (
                    <StatusBadge status={Status.ERROR} value={t("UsersTable.emailUnverified")} />
                ),
            width: 150,
        },
        {
            Header: <TextCell key="createdDate">{t("UsersTable.createdDate")}</TextCell>,
            accessor: "createdDate",
            Cell: ({ cell: { value } }) => <TextCell>{formatDateWithoutTime(value)}</TextCell>,
            width: 150,
        },
        {
            Header: <TextCell key="expirationDate">{t("UsersTable.expirationDate")}</TextCell>,
            accessor: "expirationDate",
            Cell: ({ cell: { value } }) => (
                <TextCell>{value ? formatDateWithoutTime(value) : t("Common.never")}</TextCell>
            ),
            width: 150,
        }
    );

    licensePoolsAccess &&
        columns.push({
            Header: <TextCell key="pool_name">{t("UsersTable.licensePoolAssigned")}</TextCell>,
            accessor: "poolName",
            Cell: ({ cell: { value } }) => <TextCell>{value}</TextCell>,

            width: 150,
        });

    return columns;
};
